import { EyeOutlined } from '@ant-design/icons';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  useSensor,
  useSensors,
  MouseSensor,
} from '@dnd-kit/core';
import React, { useEffect, useState } from 'react';

import { useTheme } from 'styled-components';

import { Button, Flex, Text } from 'src/client/components';
import LoadingAnimation from 'src/client/components/LoadingAnimation';
import { defaultVisibleTypes } from 'src/client/constants/pageRecipients';
import { useLocalStorage } from 'src/client/hooks/useLocalStorage';
import { useSectionStatus } from 'src/client/hooks/useSectionStatus';
import { analytics } from 'src/client/libs/segment';
import { useMovePageRecipientsEditData } from 'src/client/pages/DonorGallery/components/CardsSection/hooks/useMovePageRecipient';
import { STATUS_OPTIONS, SectionStatus } from 'src/client/types/Gallery';
import { sortAndGroupPageRecipients } from 'src/client/utils/PageRecipientUtils';
import { SEGMENT_EVENTS } from 'src/commons/constants/segment';
import {
  Page,
  PageRecipient,
  PageRecipientVisibility,
  RECIPIENT_TYPE,
} from 'src/commons/types';

import FeaturedCardsEditMode from './components/FeaturedCardsEditMode';
import FeaturedCardsViewMode from './components/FeaturedCardsViewMode';
import GiveCard from './components/GiveCard';
import HiddenCardsEditMode from './components/HiddenCardsEditMode';
import RecipientTypesFilterModal from './components/RecipientTypesFilterModal';
import TogetherViewInfoModal from './components/TogetherViewInfoModal';
import TogetherViewModal from './components/TogetherViewModal';
import UnfeaturedCardsEditMode from './components/UnfeaturedCardsEditMode';
import UnfeaturedCardsViewMode from './components/UnfeaturedCardsViewMode';

import * as S from './styles';

type Props = {
  containerRef?: HTMLDivElement | null;
  isLoading: boolean;
  isSaveButtonLoading: boolean;
  isViewingPublicly: boolean;
  isUpdatingPage: boolean;
  onSave: (updatedPageRecipients?: PageRecipient[]) => Promise<void>;
  onToggleTogetherView: (isActive: boolean) => Promise<void>;
  page: Page;
  pageRecipientsEditData: PageRecipient[];
  savedPageRecipients: PageRecipient[] | undefined;
  sectionStatus: SectionStatus;
  setPageRecipientsEditData: (newPageRecipients: PageRecipient[]) => void;
  setSectionStatus: (newStatus: STATUS_OPTIONS) => void;
  setVisibleTypes: React.Dispatch<React.SetStateAction<Set<RECIPIENT_TYPE>>>;
  visibleTypes: Set<RECIPIENT_TYPE>;
};

function CardsSection(props: Props) {
  const {
    containerRef,
    isLoading,
    isUpdatingPage,
    isViewingPublicly,
    page,
    pageRecipientsEditData,
    savedPageRecipients,
    sectionStatus,
    visibleTypes,
    setPageRecipientsEditData,
    setSectionStatus,
    setVisibleTypes,
    onSave,
    onToggleTogetherView,
  } = props;

  const { colors } = useTheme();

  const { isSectionHasChanges: isCardsSectionHasChanges } =
    useSectionStatus(sectionStatus);

  const isTogetherViewActive = page.isTogetherViewActive;

  const [activePageRecipient, setActivePageRecipient] =
    useState<PageRecipient | null>(null);
  const [isRecipientTypesFilterModalOpen, setIsRecipientTypesFilterModalOpen] =
    useState(false);
  const [tempVisibleTypes, setTempVisibleTypes] = useState(visibleTypes);

  const [isTogetherViewInfoModalOpen, setIsTogetherViewInfoModalOpen] =
    useState(false);
  const [isTogetherViewMainModalOpen, setIsTogetherViewMainModalOpen] =
    useState(false);

  const [shouldShowTogetherViewInfoModal, setShouldShowTogetherViewInfoModal] =
    useLocalStorage('shouldShowTogetherViewInfoModal', true);

  useEffect(() => {
    if (savedPageRecipients) {
      setPageRecipientsEditData(savedPageRecipients);
      setVisibleTypes(
        new Set(page.visibleRecipientTypes || defaultVisibleTypes)
      );
      setTempVisibleTypes(
        new Set(page.visibleRecipientTypes || defaultVisibleTypes)
      );
      setSectionStatus(STATUS_OPTIONS.EDITING_WITH_NO_CHANGES);

      analytics.track(
        SEGMENT_EVENTS.USER_CLICKED_AN_EDIT_GALLERY_SECTION_BUTTON,
        {
          page,
          ownerName: page.ownerName,
          section: 'cards',
        }
      );
    }
  }, [page, savedPageRecipients]);

  useEffect(() => {
    setTempVisibleTypes(visibleTypes);
  }, [visibleTypes]);

  const pageRecipients = pageRecipientsEditData;
  const unfilteredUnfeaturedPageRecipients = pageRecipients?.filter(
    (pageRecipient) =>
      pageRecipient.visibility === PageRecipientVisibility.UNFEATURED
  );

  const {
    sortedFeaturedPageRecipients: featuredPageRecipients,
    sortedUnfeaturedPageRecipients: unfeaturedPageRecipients,
    sortedHiddenPageRecipients: hiddenPageRecipients,
    sortedTogetherViewFeaturedPageRecipients:
      togetherViewFeaturedPageRecipients,
  } = sortAndGroupPageRecipients(pageRecipients, visibleTypes);

  const {
    featurePageRecipient,
    featuredCardsEditModeRef,
    hiddenCardsEditModeRef,
    hidePageRecipient,
    moveAndSetPageRecipientOrder,
    moveFeaturedPageRecipientToUnfeatured,
    moveHiddenPageRecipientToUnfeatured,
    moveTogetherViewFeaturedRecipients,
    unfeaturedCardsEditModeRef,
  } = useMovePageRecipientsEditData({
    pageRecipientsEditData,
    setPageRecipientsEditData,
    isCardsSectionHasChanges,
    setSectionStatus,
  });

  function handleDragStart(event: DragStartEvent) {
    const { active } = event;

    setActivePageRecipient(
      pageRecipientsEditData.find(
        (pageRecipient) => pageRecipient.id === active.id
      ) as PageRecipient
    );
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over) {
      return null;
    }

    const overPageRecipient = pageRecipients?.find(
      (pageRecipient) => pageRecipient.id === over?.id
    ) as PageRecipient;

    const isGoingToDropToDifferentVisibility =
      overPageRecipient.visibility !== activePageRecipient?.visibility;

    if (isGoingToDropToDifferentVisibility) {
      return null;
    }

    if (active.id !== over?.id) {
      const pageRecipientOver = pageRecipients?.find(
        (pageRecipient) => pageRecipient.id === over.id
      ) as PageRecipient;
      moveAndSetPageRecipientOrder({
        pageRecipients: pageRecipientsEditData,
        pageRecipienToMoveId: active.id as string,
        newOrderIndex: pageRecipientOver.order as number,
        newVisibility: pageRecipientOver.visibility,
      });
    }
  }

  function handleToggle(type: RECIPIENT_TYPE, isVisible: boolean) {
    setTempVisibleTypes((prev) => {
      const newSet = new Set(prev);

      if (isVisible) {
        newSet.add(type);
      } else {
        newSet.delete(type);
      }

      return newSet;
    });
  }

  function handleCancelEditingVisibleTypes() {
    setTempVisibleTypes(visibleTypes);
    setIsRecipientTypesFilterModalOpen(false);
  }

  function handleSaveVisibleTypes() {
    setVisibleTypes(tempVisibleTypes);
    setSectionStatus(STATUS_OPTIONS.EDITING_WITH_CHANGES);
    setIsRecipientTypesFilterModalOpen(false);
  }

  function handleInfoModalContinue(shouldNotShowAgain: boolean) {
    if (shouldNotShowAgain) {
      setShouldShowTogetherViewInfoModal(false);
    }

    setIsTogetherViewInfoModalOpen(false);
    setIsTogetherViewMainModalOpen(true);
  }

  function handleInfoModalCancel() {
    setIsTogetherViewInfoModalOpen(false);
  }

  function handleMainModalClose() {
    setIsTogetherViewMainModalOpen(false);
  }

  function handleMainModalOpen() {
    if (shouldShowTogetherViewInfoModal) {
      setIsTogetherViewInfoModalOpen(true);
    } else {
      setIsTogetherViewMainModalOpen(true);
    }
  }

  async function handleSaveTogetherViewSelection(
    selectedRecipients: PageRecipient[]
  ) {
    const updatedPageRecipients =
      moveTogetherViewFeaturedRecipients(selectedRecipients);

    await onSave(updatedPageRecipients);
  }

  async function handleToggleToGalleryView() {
    if (isTogetherViewActive) {
      await onToggleTogetherView(false);
    }
  }

  async function handleToggleToTogetherView() {
    if (isTogetherViewActive) {
      return;
    }

    if (shouldShowTogetherViewInfoModal) {
      setIsTogetherViewInfoModalOpen(true);
    } else {
      await onToggleTogetherView(true);
    }
  }

  const isFeaturedPageRecipientsEmpty = featuredPageRecipients?.length === 0;
  const isUnfeaturedPageRecipientsEmpty =
    unfeaturedPageRecipients?.length === 0;
  const isHiddenPageRecipientsEmpty = hiddenPageRecipients?.length === 0;

  const isEditing = !isViewingPublicly;

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const featuredCards = isEditing ? (
    <FeaturedCardsEditMode
      featuredPageRecipients={
        isTogetherViewActive
          ? togetherViewFeaturedPageRecipients
          : featuredPageRecipients
      }
      handleDragEnd={handleDragEnd}
      handleDragStart={handleDragStart}
      isLoading={isLoading}
      isTogetherViewActive={Boolean(isTogetherViewActive)}
      ref={featuredCardsEditModeRef}
      sensors={sensors}
      onHidePageRecipient={hidePageRecipient}
      onManageTogetherView={handleMainModalOpen}
      onUnfeaturePageRecipient={moveFeaturedPageRecipientToUnfeatured}
    />
  ) : (
    <FeaturedCardsViewMode
      featuredPageRecipients={
        isTogetherViewActive
          ? togetherViewFeaturedPageRecipients
          : featuredPageRecipients
      }
      isLoading={isLoading}
    />
  );
  const unfeaturedCards = isEditing ? (
    <UnfeaturedCardsEditMode
      featuredPageRecipients={featuredPageRecipients}
      handleDragEnd={handleDragEnd}
      handleDragStart={handleDragStart}
      isLoading={isLoading}
      ref={unfeaturedCardsEditModeRef}
      sensors={sensors}
      unfeaturedPageRecipients={unfeaturedPageRecipients}
      onFeaturePageRecipient={featurePageRecipient}
      onHidePageRecipient={hidePageRecipient}
    />
  ) : (
    <UnfeaturedCardsViewMode
      containerRef={containerRef}
      isLoading={isLoading}
      unfeaturedPageRecipients={unfeaturedPageRecipients}
    />
  );
  const hiddenCardsContent = isEditing ? (
    <HiddenCardsEditMode
      handleDragEnd={handleDragEnd}
      handleDragStart={handleDragStart}
      hiddenPageRecipients={hiddenPageRecipients}
      isLoading={isLoading}
      ref={hiddenCardsEditModeRef}
      sensors={sensors}
      onFeaturePageRecipient={moveHiddenPageRecipientToUnfeatured}
    />
  ) : null;

  const giveCardOverlay = activePageRecipient ? (
    <GiveCard
      isDragOverlay
      isEditMode
      id={activePageRecipient.id}
      pageRecipient={activePageRecipient}
    />
  ) : null;

  const isSaveDisabled =
    tempVisibleTypes.size === visibleTypes.size &&
    [...tempVisibleTypes].every((type) => visibleTypes.has(type));

  if (isLoading) {
    return (
      <S.LoadingContainer>
        <LoadingAnimation size="large" />
      </S.LoadingContainer>
    );
  }

  return (
    <DndContext
      autoScroll={{
        acceleration: 500,
      }}
      sensors={sensors}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
    >
      <S.GivesSection>
        <S.GivesSectionContentContainer isEditing={isEditing}>
          <S.CardsSectionContainer isEditing={isEditing}>
            {!isViewingPublicly && (
              <S.RecipientGalleryViewToggleGroup isLoading={isUpdatingPage}>
                <S.ToggleOption
                  isActive={!isTogetherViewActive}
                  onClick={handleToggleToGalleryView}
                >
                  <S.IconContainer
                    color={colors.randomizeYellow}
                    isActive={!isTogetherViewActive}
                  >
                    <img alt="Gallery view" src="/gallery-view-icon.png" />
                  </S.IconContainer>
                  <Flex flexDirection="column">
                    <Text type="label2med2">Gallery View</Text>
                    <Text type="caption1reg2">
                      A full view of all the recipients and causes you’ve
                      supported
                    </Text>
                  </Flex>
                </S.ToggleOption>
                <S.ToggleOption
                  isActive={!!isTogetherViewActive}
                  onClick={handleToggleToTogetherView}
                >
                  <S.IconContainer
                    color={colors.magenta500}
                    isActive={!!isTogetherViewActive}
                  >
                    <img alt="Together view" src="/together-view-icon.png" />
                  </S.IconContainer>
                  <Flex flexDirection="column">
                    <Text type="label2med2">Together View</Text>
                    <Text type="caption1reg2">
                      A limited view featuring up to six organizations to
                      facilitate conversations with friends & family
                    </Text>
                  </Flex>
                </S.ToggleOption>
              </S.RecipientGalleryViewToggleGroup>
            )}
            {(!isFeaturedPageRecipientsEmpty || isEditing) && (
              <>
                <S.CardsHeader isEditing={isEditing}>Featured</S.CardsHeader>
                {featuredCards}
              </>
            )}
            {(!isUnfeaturedPageRecipientsEmpty || isEditing) &&
              !isTogetherViewActive && (
                <>
                  <S.RecipientFilterHeader isEditing={isEditing}>
                    <S.CardsHeader isEditing={isEditing}>
                      Recipient Gallery
                    </S.CardsHeader>
                    {!isViewingPublicly && (
                      <Button
                        size="large"
                        type="secondary"
                        onClick={() => setIsRecipientTypesFilterModalOpen(true)}
                      >
                        <EyeOutlined />
                        Show / hide recipient type
                      </Button>
                    )}
                  </S.RecipientFilterHeader>
                  {unfeaturedCards}
                </>
              )}
            {!isHiddenPageRecipientsEmpty &&
              isEditing &&
              !isTogetherViewActive && (
                <>
                  <S.CardsHeader isEditing={isEditing}>Hidden</S.CardsHeader>
                  {hiddenCardsContent}
                </>
              )}
            <DragOverlay>{giveCardOverlay}</DragOverlay>
          </S.CardsSectionContainer>
        </S.GivesSectionContentContainer>
      </S.GivesSection>

      {isRecipientTypesFilterModalOpen && (
        <RecipientTypesFilterModal
          handleCancelEditingVisibleTypes={handleCancelEditingVisibleTypes}
          handleSaveVisibleTypes={handleSaveVisibleTypes}
          isOpen={isRecipientTypesFilterModalOpen}
          isSaveDisabled={isSaveDisabled}
          tempVisibleTypes={tempVisibleTypes}
          unfilteredUnfeaturedPageRecipients={
            unfilteredUnfeaturedPageRecipients
          }
          onCancel={() => setIsRecipientTypesFilterModalOpen(false)}
          onToggle={handleToggle}
        />
      )}

      {isTogetherViewInfoModalOpen && (
        <TogetherViewInfoModal
          open={isTogetherViewInfoModalOpen}
          onCancel={handleInfoModalCancel}
          onContinue={handleInfoModalContinue}
        />
      )}

      {isTogetherViewMainModalOpen && (
        <TogetherViewModal
          open={isTogetherViewMainModalOpen}
          pageRecipients={pageRecipients}
          onCancel={handleMainModalClose}
          onSaveSelection={handleSaveTogetherViewSelection}
          onToggleTogetherView={onToggleTogetherView}
        />
      )}
    </DndContext>
  );
}

export default CardsSection;
